(ns ncr.fragment-types.img-viewer
  (:require
   [goog.dom.classlist :as gdc]
   [goog.events :as ge]
   [goog.events.EventType :as EventType]
   [goog.functions :as gfunc]
   [goog.style :as gs]
   [ncr.etc.pf :as pf]
   [ncr.widgets.core :as w]
   [uix.core.alpha :as uix]
   ["openseadragon" :as OpenSeadragon]
   ["react-draggable" :refer [DraggableCore]]
   [ncr.fragment-types.widgets :as ftw]
   [ncr.fragment.attachment-markup :as am]
   [ncr.widgets.pf :as wpf]
   [ncr.fragment.ai-assist :as assist]))

;; Remove tooltips provided by OpenSeadragon as 'title' attribute of button.
(doseq [t ["ZoomOut" "ZoomIn" "Home" "FullPage"]]
  (.setString OpenSeadragon (str "Tooltips." t) ""))

(def ^:private zoom-pref-key
  "neckar.img-viewer.zoom")

(defn- read-zoom-pref []
  (let [zoom (.getItem js/localStorage zoom-pref-key)]
    (when (some? zoom)
      (cond
        (re-matches #"[0-9.]+" zoom)
        (js/parseFloat zoom)

        (re-matches #"(actual|page-fit|page-width)" zoom)
        (keyword zoom)

        :else
        nil))))

(defn- write-zoom-pref [zoom]
  (when (some? zoom)
    (js/localStorage.setItem zoom-pref-key
      (if (keyword? zoom) (name zoom) (str zoom)))))

(def ^:private max-image-zoom 10)
(def ^:private image-zoom-factor 0.2)
(def ^:private named-image-zooms #{:actual :page-fit :page-width})

(defn- fade-in [^js el]
  (ge/removeAll el EventType/TRANSITIONEND)
  (gdc/remove el "hidden")
  (gdc/remove el "fade-out")
  (gdc/add el "fade-in"))

(defn- fade-out [^js el]
  (when-not (or (gdc/contains el "hovered") (gdc/contains el "dragged"))
    (gdc/remove el "fade-in")
    (ge/listenOnce el EventType/TRANSITIONEND
      #(gdc/add el "hidden"))
    (gdc/add el "fade-out")))

(defn- point [x y]
  (OpenSeadragon/Point. x y))

(defn- get-fit-image-zoom [^js viewer]
  (let [cd (-> viewer .-viewport .getContainerSize)
        id (-> viewer .-world (.getItemAt 0) .-source .-dimensions)]
    (if (<= (/ (.-x id) (.-y id)) 1)
      (/ (.-y cd) (.-y id))
      (/ (.-x cd) (.-x id)))))

(defn c-viewer [fragment-name attachment url close attachments set-preview]
  (let
    [root-elem-ref (uix/ref)
     viewer-elem-ref (uix/ref)
     navigator (uix/ref)
     navigator-ref (uix/ref)
     navigator-container-ref (uix/ref)
     fade-out-container (gfunc/debounce #(fade-out @navigator-container-ref) 2500)
     selection-ref (uix/ref)
     scroll-cover-elem-ref (uix/ref)
     scroll-inner-elem-ref (uix/ref)
     scrollbar-size-ref (uix/ref)

     viewer (uix/state nil)
     viewer? #(some-> @viewer .-element some?)
     viewer-size (uix/state nil)
     viewer-ready? (uix/state false)

     scroll (uix/state {:x 0 :y 0})
     error-msg (uix/state nil)
     image-zoom (uix/state nil)
     visible-pages (uix/state nil)

     loading? (uix/state false)
     loader-id (uix/state 0)

     [set-attachment] (.get am/useStore [:set-attachment])

     get-image-dimensions
     (uix/callback
       #(-> ^js @viewer .-world (.getItemAt 0) .-source .-dimensions)
       [@viewer])

     portrait-image?
     (uix/callback
       #(let [d (get-image-dimensions)] (<= (/ (.-x d) (.-y d)) 1))
       [get-image-dimensions])

     get-image-rect
     (uix/callback
       (fn []
         (let
           [^js viewer' @viewer
            ^js viewport (.-viewport viewer')
            image-zoom (.viewportToImageZoom viewport (.getZoom viewport))
            scale #(* % image-zoom)
            dimensions (-> viewer' .-world (.getItemAt 0) .-source .-dimensions)
            left-top (.imageToViewerElementCoordinates viewport (point 0 0))]
           {:x (.-x left-top)
            :y (.-y left-top)
            :width (scale (.-x dimensions))
            :height (scale (.-y dimensions))}))
       [@viewer])

     update-scroller
     (fn []
       (.requestAnimationFrame js/window
         (fn []
           (when (viewer?)
             (let
               [{:keys [x y width height]} (get-image-rect)
                scroll-inner-elem-style (.-style @scroll-inner-elem-ref)
                scroll-cover-elem @scroll-cover-elem-ref

                rect (.getBoundingClientRect scroll-cover-elem)
                width' (.-width rect)
                height' (.-height rect)

                scroll-left (if (> width width') (* -1 x) 0)
                scroll-top (if (> height height') (* -1 y) 0)]

               (set! (.-width scroll-inner-elem-style) (str width "px"))
               (set! (.-height scroll-inner-elem-style) (str height "px"))

               (set! (.-scrollLeft scroll-cover-elem) scroll-left)
               (set! (.-scrollTop scroll-cover-elem) scroll-top)

               (reset! scroll {:x scroll-left :y scroll-top}))))))

     scroll-to-top
     (fn []
       (.requestAnimationFrame js/window
         (fn []
           (when (viewer?)
             (let [^js viewport (.-viewport @viewer)]
               (.panTo viewport
                 (point 0.5 (-> viewport .getBounds .-height (/ 2)))
                 true)
               (.requestAnimationFrame js/window #(reset! viewer-ready? true)))))))

     update-visible-pages
     (fn []
       (reset! visible-pages
         (if @viewer
           [(let
              [{:keys [width height]} (get-image-rect)]
              {:id 1
               :x 0
               :y 0
               :viewport #js {:width width :height height}})]
           nil)))

     apply-constraints
     (fn []
       (when (viewer?)
         (-> @viewer .-viewport (.applyConstraints true))
         (update-scroller)
         (update-visible-pages)))

     update-viewer
     (fn []
       (.requestAnimationFrame js/window
         (fn []
           (when (viewer?)
             (let [viewer-elem-style (.-style @viewer-elem-ref)
                   scrollbar-size (inc ; TODO Why need to add 1?
                                    (- (.-offsetHeight @scroll-cover-elem-ref)
                                      (.-clientHeight @scroll-cover-elem-ref)))]

               (when (pos? scrollbar-size)
                 (reset! scrollbar-size-ref scrollbar-size)
                 (let [bcr (.getBoundingClientRect @root-elem-ref)
                       dimension-without-scrollbar #(-> % (- scrollbar-size) (str "px"))
                       [w h] (map dimension-without-scrollbar [(.-width bcr) (.-height bcr)])]
                   (set! (.-width viewer-elem-style) w)
                   (set! (.-height viewer-elem-style) h)
                   (reset! viewer-size {:width w :height h}))))

             (apply-constraints)))))

     set-image-zoom
     (fn [value]
       (let
         [new-image-zoom
          (when (viewer?)
            (let
              [viewport ^js (.-viewport @viewer)
               vw-image-zoom (.viewportToImageZoom viewport (.getZoom viewport))]
              (cond
                (= :in value)
                (let [new-image-zoom (+ vw-image-zoom (* image-zoom-factor vw-image-zoom))]
                  (if (< new-image-zoom max-image-zoom) new-image-zoom max-image-zoom))

                (= :out value)
                (- vw-image-zoom (* image-zoom-factor vw-image-zoom))

                (contains? named-image-zooms value)
                value

                (number? value)
                (min value max-image-zoom)

                :else
                nil)))]
         (write-zoom-pref new-image-zoom)
         (reset! image-zoom new-image-zoom)))

     drag-listener
     (let [offset (uix/ref)]
       (fn [state]
         (fn [event ^js drag-data]
           (let [^js el @scroll-cover-elem-ref
                 ^js size (gs/getSize @navigator-ref)
                 [x y] (condp = state
                         :start (do
                                  (gdc/add @navigator-container-ref "dragged")
                                  (let [bounds (.. event -target getBoundingClientRect)
                                        offs [(- (.-clientX event) (.-left bounds))
                                              (- (.-clientY event) (.-top bounds))]]
                                    (reset! offset offs)
                                    offs))
                         :stop (do
                                 (gdc/remove @navigator-container-ref "dragged")
                                 (fade-out-container)
                                 @offset)
                         @offset)]
             (set! (.-scrollLeft el) (* (/ (.-scrollWidth el) (.-width size)) (- (.-x drag-data) x)))
             (set! (.-scrollTop el) (* (/ (.-scrollHeight el) (.-height size)) (- (.-y drag-data) y)))))))]

    (uix/with-effect [attachment]
      (set-attachment attachment))

    (uix/with-effect [url @loader-id]
      (reset! loading? true)
      (reset! viewer-ready? false)
      (let

       ;; Ideally we want this as a plugin, see http://openseadragon.github.io/#plugins
        [navigator'
         (OpenSeadragon/Viewer.
           #js {:element @navigator-ref
                :mouseNavEnabled false
                :showNavigationControl false
                :defaultZoomLevel 0 ; fit to viewer
                :animationTime 0
                :tileSources
                #js {:type "image"
                     :url url
                     :buildPyramid false}})

         viewer'
         (OpenSeadragon
           #js {:element @viewer-elem-ref
                :showZoomControl false
                :showHomeControl false
                :showFullPageControl false
                :fullPageButton "ncr-c-img-viewer__fullscreen"
                :zoomPerClick 1
                :zoomPerScroll 1
                :zoomPerDblClickDrag 1
                :preserveImageSizeOnResize true

                ;; remove excessive canvas
                :visibilityRatio 1
                :constrainDuringPan true

                :minZoomImageRatio 0
                :maxZoomPixelRatio "Infinity"
                :tileSources
                #js {:type "image"
                     :url url
                     :buildPyramid false}})

         update-selection
         (fn []
           (when-let [^js style (and @selection-ref (.-style @selection-ref))]
             (let [^js viewport-bounds (.getBounds (.-viewport viewer') true)
                   ^js tl (.viewportToImageCoordinates (.-viewport viewer') (.getTopLeft viewport-bounds))
                   ^js br (.viewportToImageCoordinates (.-viewport viewer') (.getBottomRight viewport-bounds))
                   ^js image-dimensions (get-image-dimensions)
                   ^js navigator-size (gs/getContentBoxSize @navigator-ref)
                   navigator-scale (if (> (.-width navigator-size) (.-height navigator-size))
                                     (/ (.-x image-dimensions) (.-width navigator-size))
                                     (/ (.-y image-dimensions) (.-height navigator-size)))
                   x (/ (max (.-x tl) 0) navigator-scale)
                   y (/ (max (.-y tl) 0) navigator-scale)
                   w (- (/ (min (.-x br) (.-x image-dimensions)) navigator-scale) x)
                   h (- (/ (min (.-y br) (.-y image-dimensions)) navigator-scale) y)
                   container @navigator-container-ref]
               (fade-in container)
               (set! (.-left style) (str x "px"))
               (set! (.-top style) (str y "px"))
               (set! (.-width style) (str w "px"))
               (set! (.-height style) (str h "px")))
             (fade-out-container)))

         scroll-cover-elem @scroll-cover-elem-ref

         scroll-event-listener
         (fn [_]
           (let
             [^js el @scroll-cover-elem-ref
              {:keys [x y]} @scroll
              new-scroll-left (.-scrollLeft el)
              new-scroll-top (.-scrollTop el)]
             (reset! scroll {:x new-scroll-left :y new-scroll-top})

             (when (viewer?)
               (let [^js viewport (.-viewport @viewer)]
                 (.panBy viewport
                   (.deltaPointsFromPixels viewport
                     (point (- new-scroll-left x) (- new-scroll-top y)))
                   true)))))]

        (.addHandler navigator' "open"
          #(reset! navigator navigator'))

        (.addHandler viewer' "open"
          (fn []
            (reset! error-msg nil)
            (reset! loading? false)
            (reset! viewer viewer')

            (update-viewer)
            (.requestAnimationFrame js/window
              (fn []
                (set-image-zoom (read-zoom-pref))
                (scroll-to-top)))
            (.addEventListener scroll-cover-elem EventType/SCROLL scroll-event-listener)))

        (.addHandler viewer' "open-failed"
          (fn [e]
            (reset! error-msg e.message)
            (reset! loading? false)))

        (.addHandler viewer' "close"
          (fn []
            (.removeEventListener scroll-cover-elem EventType/SCROLL scroll-event-listener)
            (reset! image-zoom nil)))

        (.addHandler viewer' "animation" update-selection)
        (.addHandler viewer' "animation-finish" update-visible-pages)

        (.addHandler viewer' "destroy"
          (fn [_]
            (.destroy navigator')
            (reset! viewer nil)))

        #(.destroy ^js @viewer)))

    (uix/with-effect [@image-zoom @viewer-size @viewer @navigator portrait-image?]
      (when (viewer?)
        (let
          [^js viewport (.-viewport @viewer)
           image-dimensions (get-image-dimensions)
           image-zoom' @image-zoom

           zoom-to
           (fn [image-zoom-value]
             (.zoomTo viewport
               (.imageToViewportZoom ^js viewport image-zoom-value)
               nil
               true)
             (apply-constraints)
             (update-scroller))]

          (let [viewer-size (gs/getSize @viewer-elem-ref)
                image-ratio (/ (.-x image-dimensions) (.-y image-dimensions))
                viewer-ratio (/ (.-width viewer-size) (.-height viewer-size))
                [viewer-side image-side] (if (< image-ratio viewer-ratio)
                                           [(.-height viewer-size) (.-y image-dimensions)]
                                           [(.-width viewer-size) (.-x image-dimensions)])
                navigator-scale (/ image-side (* viewer-side 0.4))
                w (/ (.-x image-dimensions) navigator-scale)
                h (/ (.-y image-dimensions) navigator-scale)]
            (let [style (.-style @navigator-container-ref)]
              (set! (.-width style) (str w "px"))
              (set! (.-position style) "absolute")
              (set! (.-height style) (str h "px")))
            (.requestAnimationFrame js/window #(.goHome (.-viewport ^js @navigator))))

          (cond
            (nil? image-zoom')
            (zoom-to (.viewportToImageZoom viewport 0.5))

            (number? image-zoom')
            (zoom-to image-zoom')

            (= :actual image-zoom')
            (zoom-to 1)

            (= :page-fit image-zoom')
            (let
              [portrait? (portrait-image?)

               image-dim (if portrait? (.-y image-dimensions) (.-x image-dimensions))

               viewer-elem-dimensions (.getBoundingClientRect @viewer-elem-ref)
               viewer-elem-dim (if portrait? (.-height viewer-elem-dimensions) (.-width viewer-elem-dimensions))]
              (zoom-to (/ viewer-elem-dim image-dim)))

            (= :page-width image-zoom')
            (let
              [viewer-elem-width (-> @viewer-elem-ref .getBoundingClientRect .-width)]
              (zoom-to (/ viewer-elem-width (.-x (get-image-dimensions)))))))))

    (uix/with-effect [@root-elem-ref]
      (fn []
        (when-let [root-elem @root-elem-ref]
          (let
            [container-resize-observer
             (new js/ResizeObserver
               (gfunc/debounce
                 (fn [_]
                   (.requestAnimationFrame js/window
                     (fn []
                       (when (viewer?)
                         (update-viewer)))))
                 30))]
            (.observe container-resize-observer root-elem)
            (fn []
              (.disconnect container-resize-observer))))))

    (w/use-event navigator-container-ref EventType/MOUSEENTER
      (fn [_]
        (when-let [container @navigator-container-ref]
          (gdc/add container "hovered")
          (fade-in container))))

    (w/use-event navigator-container-ref EventType/MOUSELEAVE
      (fn [_]
        (when-let [container @navigator-container-ref]
          (gdc/remove container "hovered")
          (fade-out-container))))

    [:<>
     [ftw/c-toolbar fragment-name attachment close attachments set-preview
      [:<>
       [pf/c-toolbar-group {:variant :icon-button-group}
        [w/c-toolbar-button
         {:icon :magnify-minus-outline
          :on-click #(set-image-zoom :out)
          :tooltip "Zoom Out"}]

        [w/c-toolbar-button
         {:icon :magnify-plus-outline
          :on-click #(set-image-zoom :in)
          :tooltip "Zoom In"}]

        (let
          [image-zoom' @image-zoom
           options
           [{:id :0.5 :label "50%"}
            {:id :0.75 :label "75%"}
            {:id :1 :label "100%"}
            {:id :1.25 :label "125%"}
            {:id :1.50 :label "150%"}
            {:id :2 :label "200%"}
            {:id :3 :label "300%"}
            {:id :4 :label "400%"}
            {:id :actual :label "Actual Size"}
            {:id :page-fit :label "Page Fit"}
            {:id :page-width :label "Page Width"}]
           selected (some #(when (= image-zoom' (:id %)) %) options)
           placeholder-text
           (cond
             (nil? image-zoom') "Default"
             selected (:label selected)
             :else (-> image-zoom' (* 100) .toFixed (str "%")))]
          [wpf/c-pf-select
           {:variant :single
            :placeholder-text placeholder-text
            :selections selected
            :on-select
            (fn [_ {:keys [id]} _]
              (set-image-zoom
                (as-> (keyword id) $
                  (if (contains? named-image-zooms $) $ (js/parseFloat id)))))}
           options])]

       [pf/c-toolbar-group {:variant :icon-button-group}
        [am/toolbar-icon]
        (let [{:keys [auxdata reference-id]} attachment]
          [assist/toolbar-button {:reference reference-id :data (:assist auxdata)}])]]]

     [ftw/c-content @error-msg @loading? #(swap! loader-id inc)
      (let [transparent-style (when-not @viewer-ready? {:opacity 0})]
        [:div.ncr-c-img-viewer {:ref root-elem-ref}
         [:div.ncr-c-img-viewer-viewer {:ref viewer-elem-ref :style transparent-style}]
         [:div.ncr-c-img-viewer-scroll-cover {:ref scroll-cover-elem-ref :style transparent-style}
          [:div.ncr-c-img-viewer-scroll-inner
           {:ref scroll-inner-elem-ref
            :style
            (when (viewer?)
              (let [{:keys [x y]} (get-image-rect)]
                {:margin-left (if (neg? x) 0 x)
                 :margin-top (if (neg? y) 0 y)}))}
           [am/markup-viewer {:attachments attachments :pages @visible-pages}]]]
         [:div.ncr-c-img-viewer-navigator-container.fade-out.hidden
          {:ref navigator-container-ref :style {:position "absolute" :top 15 :right (+ 15 @scrollbar-size-ref)}}
          [:div.ncr-c-img-viewer-navigator {:ref navigator-ref}]
          [:> DraggableCore {:on-start (drag-listener :start) :on-stop (drag-listener :stop) :on-drag (drag-listener :drag)}
           [:div.ncr-c-img-viewer-selection {:ref selection-ref}]]]])]]))
